import { useMemo } from "react";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { DeploySteps } from "@rapid/types/flows";
import type { InferenceStrategyConfig } from "./types";
import { BuildFlowStrategy } from "./BuildFlowStrategy";
import { ImproveFlowStrategy } from "./ImproveFlowStrategy";

// Singleton instances to avoid unnecessary object creation
const buildStrategy = new BuildFlowStrategy();
const improveStrategy = new ImproveFlowStrategy();

/**
 * Hook to get the appropriate inference strategy for the current flow.
 *
 * This hook encapsulates all the flow-specific inference logic,
 * eliminating scattered `if (isImproveFlow)` conditionals throughout the codebase.
 *
 * @returns The inference strategy configuration for the current flow
 *
 * @example
 * const strategy = useInferenceStrategy();
 * const sam3Labels = strategy.getSam3Labels(promptLabels, textPrompts);
 * const shouldAppend = strategy.shouldAppendPredictions(false);
 */
export function useInferenceStrategy(): InferenceStrategyConfig {
    const { step } = useFlowState();

    return useMemo(() => {
        if (step === DeploySteps.IMPROVE) {
            return improveStrategy;
        }
        return buildStrategy;
    }, [step]);
}
